/**
 * Refresh Stage 1: Audit — displays the refresh plan from the LLM.
 *
 * Shows the article's current state (from document pre-population) as inputs,
 * and the audit results (diagnosis, section assessments, suggestions) as output.
 */

import React from 'react';
import { StagePanel } from '../StagePanel';
import { useArticleContext } from '../ArticleContext';
import type { ArticleDocument } from '../types';

function ArticleSummary() {
  const { document } = useArticleContext();
  if (!document) return null;

  const article = document.article || {};
  const perf = (document as any).performance_context || {};
  const refresh = (document as any).refresh || {};
  const md = article.markdown || '';
  const sections = article.sections || [];

  return (
    <div className="space-y-2 text-xs">
      {refresh.source_url && (
        <div className="bg-gray-50 rounded border p-2">
          <div><span className="text-gray-400">URL:</span> <a href={refresh.source_url} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">{refresh.source_url}</a></div>
          {refresh.original_published_at && <div><span className="text-gray-400">Published:</span> {new Date(refresh.original_published_at).toLocaleDateString()}</div>}
          <div><span className="text-gray-400">Article length:</span> {md.length.toLocaleString()} chars</div>
          <div><span className="text-gray-400">Sections:</span> {sections.length}</div>
        </div>
      )}
      {(perf.clicks != null || perf.impressions != null) && (
        <div className="bg-amber-50 rounded border border-amber-200 p-2">
          <div className="text-amber-800 font-medium mb-1">Performance Context</div>
          <div className="text-amber-700 space-y-0.5">
            {perf.clicks != null && <div>Clicks (28d): {perf.clicks}</div>}
            {perf.impressions != null && <div>Impressions: {perf.impressions?.toLocaleString()}</div>}
            {perf.avg_position != null && <div>Avg position: {perf.avg_position}</div>}
            {perf.ctr != null && <div>CTR: {(perf.ctr * 100).toFixed(2)}%</div>}
          </div>
          {perf.health_issues?.length > 0 && (
            <div className="mt-1 pt-1 border-t border-amber-200 text-amber-600">
              {perf.health_issues.map((issue: string, i: number) => (
                <div key={i}>• {issue}</div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

const ACTION_COLORS: Record<string, string> = {
  keep: 'bg-green-50 text-green-700 border-green-200',
  update: 'bg-yellow-50 text-yellow-700 border-yellow-200',
  rewrite: 'bg-red-50 text-red-700 border-red-200',
  remove: 'bg-gray-100 text-gray-500 border-gray-200 line-through',
};

function renderOutput(doc: ArticleDocument) {
  const audit = (doc as any).refresh?.audit;
  if (!audit) return <div className="text-sm text-gray-500">No audit results yet. Run this stage to analyze the article.</div>;

  return (
    <div className="space-y-4 text-sm">
      {/* Diagnosis */}
      <div className={`rounded border p-3 ${
        audit.strategy === 'retire' ? 'bg-red-50 border-red-200'
          : audit.strategy === 'consolidate' ? 'bg-amber-50 border-amber-200'
          : 'bg-blue-50 border-blue-200'
      }`}>
        <div className={`font-medium mb-1 ${
          audit.strategy === 'retire' ? 'text-red-800'
            : audit.strategy === 'consolidate' ? 'text-amber-800'
            : 'text-blue-800'
        }`}>Diagnosis</div>
        <p className={audit.strategy === 'retire' ? 'text-red-900' : audit.strategy === 'consolidate' ? 'text-amber-900' : 'text-blue-900'}>{audit.diagnosis}</p>
        <div className={`mt-2 inline-block px-2 py-0.5 rounded text-xs font-medium ${
          audit.strategy === 'retire' ? 'bg-red-100 text-red-700'
            : audit.strategy === 'consolidate' ? 'bg-amber-100 text-amber-700'
            : audit.strategy === 'structural_rework' ? 'bg-orange-100 text-orange-700'
            : audit.strategy === 'content_update' ? 'bg-blue-100 text-blue-700'
            : 'bg-green-100 text-green-700'
        }`}>
          Strategy: {audit.strategy}
        </div>
        {audit.consolidation_target && (
          <div className="mt-2 text-sm text-amber-800">
            <span className="font-medium">Consolidate into:</span> {audit.consolidation_target}
          </div>
        )}
        {audit.retirement_reason && (
          <div className="mt-2 text-sm text-red-800">
            <span className="font-medium">Retirement reason:</span> {audit.retirement_reason}
          </div>
        )}
      </div>

      {/* Section assessments */}
      {audit.sections_assessment?.length > 0 && (
        <div>
          <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Existing Sections</h4>
          <div className="space-y-1.5">
            {audit.sections_assessment.map((s: any, i: number) => (
              <div key={i} className={`rounded border px-3 py-2 ${ACTION_COLORS[s.action] || 'bg-gray-50 text-gray-700 border-gray-200'}`}>
                <div className="flex items-center gap-2">
                  <span className="text-xs font-bold uppercase">{s.action}</span>
                  <span className="font-medium">{s.heading}</span>
                </div>
                {s.reason && <div className="text-xs mt-0.5 opacity-80">{s.reason}</div>}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* New sections */}
      {audit.new_sections?.length > 0 && (
        <div>
          <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Suggested New Sections</h4>
          <div className="space-y-1.5">
            {audit.new_sections.map((s: any, i: number) => (
              <div key={i} className="rounded border border-cyan-200 bg-cyan-50 px-3 py-2 text-cyan-800">
                <div className="font-medium">+ {s.heading}</div>
                {s.rationale && <div className="text-xs mt-0.5 opacity-80">{s.rationale}</div>}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Recommended keywords */}
      {doc.targeting?.keywords?.primary && (
        <div className="bg-cyan-50 rounded border border-cyan-200 p-3">
          <div className="font-medium text-cyan-800 mb-1 text-xs">Recommended Keywords (editable in Document tab)</div>
          <div className="flex flex-wrap gap-1.5">
            <span className="text-xs bg-cyan-100 text-cyan-800 px-2 py-0.5 rounded font-medium">
              Primary: {typeof doc.targeting.keywords.primary === 'string' ? doc.targeting.keywords.primary : doc.targeting.keywords.primary?.keyword}
            </span>
            {(doc.targeting?.keywords?.secondary || []).map((kw: any, i: number) => (
              <span key={i} className="text-xs bg-cyan-50 text-cyan-700 px-2 py-0.5 rounded border border-cyan-200">
                {typeof kw === 'string' ? kw : kw?.keyword}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Keyword + Meta assessments */}
      <div className="grid grid-cols-2 gap-3">
        {audit.keyword_assessment && (
          <div className="bg-gray-50 rounded border p-2 text-xs">
            <div className="font-medium text-gray-700 mb-1">Keyword Assessment</div>
            <div className="text-gray-600">
              Status: <span className="font-medium">{audit.keyword_assessment.status}</span>
            </div>
            {audit.keyword_assessment.notes && <div className="text-gray-500 mt-0.5">{audit.keyword_assessment.notes}</div>}
          </div>
        )}
        {audit.meta_assessment && (
          <div className="bg-gray-50 rounded border p-2 text-xs">
            <div className="font-medium text-gray-700 mb-1">Meta Tags</div>
            <div className="text-gray-600">
              Title: <span className="font-medium">{audit.meta_assessment.title_action}</span> ·
              Desc: <span className="font-medium">{audit.meta_assessment.description_action}</span>
            </div>
            {audit.meta_assessment.notes && <div className="text-gray-500 mt-0.5">{audit.meta_assessment.notes}</div>}
          </div>
        )}
      </div>

      {/* Flags */}
      <div className="flex gap-3 text-xs">
        <span className={`px-2 py-1 rounded ${audit.internal_links_needed ? 'bg-amber-50 text-amber-700' : 'bg-green-50 text-green-700'}`}>
          Internal links: {audit.internal_links_needed ? 'Needed' : 'OK'}
        </span>
        <span className={`px-2 py-1 rounded ${audit.research_needed ? 'bg-amber-50 text-amber-700' : 'bg-green-50 text-green-700'}`}>
          Research: {audit.research_needed ? 'Needed' : 'OK'}
        </span>
      </div>
    </div>
  );
}

export function RefreshAuditV2() {
  return (
    <StagePanel
      stageId="refresh_1_audit"
      isLlmStage
      title="Refresh: Audit"
      description="Analyzes the existing article against current performance data and produces a refresh plan."
      renderOutput={renderOutput}
    >
      <ArticleSummary />
    </StagePanel>
  );
}
